<template>
  <v-app class="vue3">
    <!-- Blank layout: no shell, just the route content -->
    <v-main v-if="isBlankLayout">
      <router-view />
    </v-main>

    <!-- Default layout: full app shell -->
    <template v-else>
      <v-main>
        <navigation-drawer v-if="logged" :is-msp-route="isMspRoute" />
        <app-bar
          v-if="logged && workspace.workspaceId"
          :app-title="appTitle"
          :is-msp-route="isMspRoute"
        />
        <v-container fluid class="pa-0">
          <!--
            One content area for every page; only its styling changes with login/sidebar state:
            - The container always drops its default padding (pa-0).
            - Logged-in pages get coro-app-content (inner padding + max-width); logged-out
              pages (login, reset password) instead stretch the content to full height
              (h-screen) so they render full-bleed.
            - When a sidebar (Setup Hub / AI Assistant) is open, the page content and the
              sidebar sit side by side in a flex row (d-flex ga-4) with the content growing to
              fill the remaining width (flex-grow-1); otherwise the content stays a plain block.
          -->
          <div :class="{ 'coro-app-content': logged, 'd-flex ga-4': isAnySidebarOpen }">
            <div :class="{ 'flex-grow-1 app-page-content': isAnySidebarOpen, 'h-screen': !logged }">
              <router-view />
            </div>
            <div class="app-right-sidebar" v-if="isAnySidebarOpen">
              <setup-hub-sidebar />
              <ai-assistant-sidebar />
            </div>
          </div>
        </v-container>
        <setup-hub-toggle />
      </v-main>
      <CoroDialog
        :config="dialogConfig"
        :value="showDialog"
        v-if="showDialog"
        @onClose="closeDialog"
      />
      <CoroSnackbar />
      <floating-go-back-button
        v-if="logged"
        :show="shouldShowGoBackButton()"
        @go-back="goBackToSecurityGaps"
        @dismiss="clearNavigationState"
      />
    </template>
  </v-app>
</template>

<script lang="ts">
import AppBar from "@/components/AppBar.vue";
import CoroSnackbar from "@/components/CoroSnackbar.vue";
import CoroDialog from "@/components/modals/CoroDialog.vue";
import FloatingGoBackButton from "@/components/FloatingGoBackButton.vue";

import { computed, defineComponent, onMounted, ref, watch } from "vue";
import { useErrorsStore } from "@/_store/errors.module";
import { SnackbarTypes, useSnackbarStore } from "@/_store/snackbar.module";
import { useDialogsStore } from "@/_store/dialogs.module";
import { storeToRefs } from "pinia";
import { useMyAccountStore } from "@/_store/my-account.module";
import { handleFaviconAndTitle } from "@/_helpers/dom";
import { extractCoroSubdomain } from "@/_helpers/workspace";
import { isMspPortalRoute } from "@/_helpers/subscription";
import ErrorModal from "@/components/modals/ErrorModal.vue";
import { ModalWidth } from "@/constants/modals";
import { useAccountStore } from "@/_store/account.module.ts";
import NavigationDrawer from "@/components/NavigationDrawer.vue";
import { useRoute } from "vue-router";
import { RouteName, RouteLayout } from "@/constants/routes";
import { i18n, setI18nLocale } from "@/plugins/i18n";
import moment from "moment/moment";
import { WorkspaceLocale, WorkspaceType } from "@/constants/workplaces";
import { useRedirectsStore } from "@/_store/redirects.module.ts";
import TrialStartedModal from "@/components/modals/TrialStartedModal.vue";
import { TutorialAction } from "@/constants/tutorial.ts";
import { useTutorialStore } from "@/_store/tutorial.module.ts";
import { useGtm } from "@gtm-support/vue-gtm";
import { GtmAction } from "@/constants/gtm.ts";
import { useWorkspaceStore } from "@/_store/workspace.module.ts";
import { useSecurityGapsNavigation } from "@/composables/useSecurityGapsNavigation.ts";
import { useAiAssistantStore } from "@/_store/ai-assistant.module.ts";
import { useAppsManagementModule } from "@/_store/apps-mgmt.module.ts";
import { useSidebarPanelStore } from "@/_store/sidebar-panel.module.ts";
import { AppContext } from "@/types.ts";
import SetupHubSidebar from "@/components/setup-hub/sidebar/SetupHubSidebar.vue";
import SetupHubToggle from "@/components/setup-hub/SetupHubToggle.vue";
import AiAssistantSidebar from "@/components/ai-assistant/AiAssistantSidebar.vue";
import { dialogsConfigConstructor } from "@/_helpers/dialog-helpers.ts";
import { setCoralogixUserContext } from "@/_helpers/analytics.ts";

export default defineComponent({
  components: {
    SetupHubSidebar,
    SetupHubToggle,
    AiAssistantSidebar,
    NavigationDrawer,
    CoroSnackbar,
    CoroDialog,
    AppBar,
    FloatingGoBackButton,
  },
  setup() {
    const errorsStore = useErrorsStore();
    const snackbarStore = useSnackbarStore();
    const dialogsStore = useDialogsStore();
    const myAccountStore = useMyAccountStore();
    const accountStore = useAccountStore();
    const tutorialStore = useTutorialStore();
    const workspaceStore = useWorkspaceStore();
    const aiAssistantStore = useAiAssistantStore();
    const appsManagementModule = useAppsManagementModule();
    const sidebarPanelStore = useSidebarPanelStore();
    const gtm = useGtm();
    const { workspace, workspaceAndPermissionsUpdatedTimestamp } = storeToRefs(workspaceStore);
    const { isAnySidebarOpen } = storeToRefs(sidebarPanelStore);
    const { showTrialModeStartedNotice } = storeToRefs(tutorialStore);
    const { handleSocAutomationAfterLogin, handleRedirectFromReportAfterLogin } =
      useRedirectsStore();
    const { closeDialog, openDialog } = dialogsStore;
    const { showDialog, dialogConfig } = storeToRefs(dialogsStore);
    const { error, errors } = storeToRefs(errorsStore);
    const { add } = snackbarStore;
    const { logged, displayLanguage } = storeToRefs(accountStore);
    const workspaceTimestamp = ref(0);
    const route = useRoute();

    // Security gaps navigation
    const { shouldShowGoBackButton, goBackToSecurityGaps, clearNavigationState } =
      useSecurityGapsNavigation();

    const subdomain = computed(() => {
      return extractCoroSubdomain(window.location.hostname);
    });

    const isBlankLayout = computed(() => route.meta.layout === RouteLayout.BLANK);

    const isMspRoute = computed(() => isMspPortalRoute());

    const appTitle = computed(() => {
      if (isMspRoute.value) {
        return i18n.global.t("general.workspacesManagement");
      }
      if (workspace.value.parentWorkspaceName) {
        return workspace.value.parentWorkspaceName;
      }

      if (workspace.value.type === WorkspaceType.CHANNEL) {
        return workspace.value.name;
      }

      return i18n.global.t("general.coroConsole");
    });

    const currentRootRouteName = computed(() => {
      return route.matched[0]?.name as RouteName | undefined;
    });

    watch(subdomain, (newVal) => {
      handleFaviconAndTitle(newVal!);
    });

    watch(
      () => route.name,
      (currentRouteName) => {
        const brandedRoutesNames = [
          RouteName.INVITED_PAGE,
          RouteName.LOGIN,
          RouteName.RESET_PASSWORD,
          RouteName.WORKSPACES,
          RouteName.FORGOT_PASSWORD,
          RouteName.SSO_PAGE,
          RouteName.DOWNLOADS_PAGE,
          RouteName.OAUTH_CONSENT,
        ];
        if (brandedRoutesNames.includes(currentRouteName as RouteName)) {
          subdomain.value
            ? workspaceStore.requestBrandingInfo(subdomain.value)
            : workspaceStore.$patch((state) => {
                state.brandingAlias = subdomain.value ?? "";
              });
        }
      }
    );

    watch(
      showTrialModeStartedNotice,
      (value) => {
        if (value) {
          const trialStartedModalConfig = {
            width: ModalWidth.MEDIUM,
            header: { close: false },
            content: {
              component: TrialStartedModal,
            },
            footer: {
              buttons: [
                {
                  title: i18n.global.t(`modals.${TutorialAction.TRIAL_HAS_STARTED}.actionBtn`),
                  color: "primary",
                  spacer: "before",
                  callback: async () => {
                    await tutorialStore.resetTrialNotice();
                    dialogsStore.closeDialog();
                    if (gtm?.enabled()) {
                      window.dataLayer?.push({ event: GtmAction.CONVERT_TO_TRIAL_CLICKED });
                    }
                  },
                  cancel: false,
                },
              ],
            },
            disable: false,
            disclaimer: null,
          };
          const config = dialogsConfigConstructor(trialStartedModalConfig);
          dialogsStore.openDialog(config);
        }
      },
      { immediate: true }
    );

    const openErrorModal = (errorMessage: string) => {
      const errorModal = {
        width: ModalWidth.SMALL,
        header: undefined,
        content: {
          component: ErrorModal,
        },
        footer: null,
        data: {
          item: errorMessage,
        },
      };
      const dialogConfig = dialogsConfigConstructor(errorModal);
      openDialog(dialogConfig);
    };

    watch(
      () => [...errors.value],
      (newValue, previousValue) => {
        // If error is null or popping errors from store - do nothing
        if (
          !error.value ||
          error.value.customErrorHandling ||
          newValue.length < previousValue.length
        )
          return;
        const errorMessage = i18n.global.te(`errors.${error.value.message}`)
          ? i18n.global.t(`errors.${error.value.message}`)
          : error.value.message;
        const showModal = error.value.showDialog;
        if (showModal) {
          openErrorModal(errorMessage);
        } else {
          const snackbarMessage = {
            html: errorMessage,
            type: SnackbarTypes.ERROR,
          };
          add(snackbarMessage);
        }
      },
      { deep: true }
    );

    // logging out in other open tabs if logout event happened
    window.addEventListener("storage", (event) => {
      if (event.key === "logout-event") {
        accountStore.$reset();
        workspaceStore.$reset();
        aiAssistantStore.$reset();
        window.location.reload();
      }

      if (event.key === "report-navigation") {
        window.location.reload();
      }
    });

    onMounted(async () => {
      if (logged.value && workspace.value.workspaceId) {
        await workspaceStore.getCurrentWorkspace();
        setCoralogixUserContext();
      }
      handleFaviconAndTitle(subdomain.value!);
    });

    watch(
      () => workspaceAndPermissionsUpdatedTimestamp.value,
      (newVal) => {
        if (newVal) {
          setCoralogixUserContext();
        }
      }
    );

    watch(
      () => logged.value,
      async (newVal) => {
        if (newVal) {
          await handleSocAutomationAfterLogin();
          await handleRedirectFromReportAfterLogin();
          await myAccountStore.getProfileData();
        }
      },
      { immediate: true }
    );

    // Non-English messages are fetched on demand, so moment only follows once vue-i18n has
    // actually switched — otherwise dates could be in a language the rest of the UI never reached
    // (a failed or superseded load). Vuetify needs no update: its vue-i18n adapter reads the same
    // `i18n.global.locale`.
    const applyDisplayLanguage = async (language: WorkspaceLocale) => {
      if (await setI18nLocale(language)) moment.locale(language);
    };

    watch(
      displayLanguage,
      (value, previousValue) => {
        if (value !== previousValue) applyDisplayLanguage(value);
      },
      { immediate: true }
    );

    // A language whose messages failed to load (a dropped connection) is retried on the next
    // navigation instead of leaving the rest of the session in English.
    watch(
      () => route.fullPath,
      () => {
        if (i18n.global.locale.value !== displayLanguage.value) {
          applyDisplayLanguage(displayLanguage.value);
        }
      }
    );

    // Keep the MSP vs workspace context aligned with the route, including on initial load
    watch(
      currentRootRouteName,
      (newRootName) => {
        if (newRootName) {
          const context =
            newRootName === RouteName.MSP ? AppContext.MSP : AppContext.SINGLE_WORKSPACE;
          appsManagementModule.setContext(context);
        }
      },
      { immediate: true }
    );

    return {
      isBlankLayout,
      workspace,
      errors,
      dialogConfig,
      showDialog,
      closeDialog,
      logged,
      workspaceTimestamp,
      isMspRoute,
      appTitle,
      isAnySidebarOpen,
      shouldShowGoBackButton,
      goBackToSecurityGaps,
      clearNavigationState,
    };
  },
});
</script>

<style lang="scss" scoped>
/* Pin the open sidebar to the viewport so it stays visible while the page scrolls.
   position: sticky can't be used here: the cui base styles set body { overflow: hidden },
   which makes body the nearest scroll container while the page actually scrolls on html,
   so sticky never engages. position: fixed with only `top` set keeps the panel's natural
   horizontal position from the flex row, while the wrapper itself stays in flow to
   reserve the 390px column */
.app-right-sidebar {
  min-width: 390px;

  > * {
    position: fixed;
    top: 56px;
    width: 390px;
  }
}

/* Allow the page content to shrink below its intrinsic width when a sidebar is open,
   so wide tables scroll horizontally instead of stretching the layout */
.app-page-content {
  min-width: 0;
}
</style>
